import { fetchWidgetIntializer } from "../components/Pages/MainPage/Prefetch";
import { getIsAccountAvailable } from "./AuthenticatorApi";
import { isFeatureEnabledInternal } from "./VelocityApi";
import { tryGetHostApi } from "./externalApi";
import { setIsMeteredConnectionUsageAllowedForMigration } from "./DashboardHostApi";
// Desyncronize IDashboardHostApi
class DashboardHostApi {
    constructor() {
        this._eventListeners = new Map();
        this._eventListeners.set("networkstatuschanged", new Array());
        this._eventListeners.set("sessionidchanged", new Array());
        this.isInCTARegion = false;
        this.isPrivacyConsentAccepted = false;
        this.isInternetConnectionAvailable = true;
        this.networkCostConstraint = "optIn";
        this.sessionId = "";
        this.hostAppVersion = "";
        this.userGeoName = "";
        this.userDisplayLocale = "";
        this.isTextDirectionRTL = false;
        this.isWVVersionBlocked = false;
        this.isAppVersionBlocked = false;
        this.isOSVersionBlocked = false;
        this.geoLocationCode = "";
        this._hostApi = null;
    }
    acceptPrivacyNotice() {
        var _a;
        (_a = this._hostApi) === null || _a === void 0 ? void 0 : _a.acceptPrivacyNotice();
    }
    requestRefresh() {
        var _a;
        (_a = this._hostApi) === null || _a === void 0 ? void 0 : _a.requestRefresh();
    }
    setLaunchAttributionContext(context) {
        var _a;
        (_a = this._hostApi) === null || _a === void 0 ? void 0 : _a.setLaunchAttributionContext(context);
    }
    requestClearCache() {
        var _a;
        (_a = this._hostApi) === null || _a === void 0 ? void 0 : _a.requestClearCache();
    }
    addEventListener(eventName, listener) {
        this._eventListeners.get(eventName).push(listener);
    }
    removeEventListener(eventName, listener) {
        const listenerList = this._eventListeners.get(eventName);
        let index = listenerList.indexOf(listener);
        if (index > -1) {
            listenerList.splice(index, 1);
        }
    }
    setIsMeteredConnectionUsageAllowedForMigration(enabled) {
        var _a;
        (_a = this._hostApi) === null || _a === void 0 ? void 0 : _a.setIsMeteredConnectionUsageAllowedForMigration(enabled);
    }
    async initAsync() {
        this._hostApi = (await tryGetHostApi("DashboardHost", 1, 1));
        if (this._hostApi) {
            const [isInCTARegion, isPrivacyConsentAccepted, isInternetConnectionAvailable, networkCostConstraint, sessionId, hostAppVersion, userGeoName, userDisplayLocale, isTextDirectionRTL, isWVVersionBlocked, isAppVersionBlocked, isOSVersionBlocked, geoLocationCode] = await Promise.all([
                this._hostApi.isInCTARegion,
                this._hostApi.isPrivacyConsentAccepted,
                this._hostApi.isInternetConnectionAvailable,
                this._hostApi.networkCostConstraint,
                this._hostApi.sessionId,
                this._hostApi.hostAppVersion,
                this._hostApi.userGeoName,
                this._hostApi.userDisplayLocale,
                this._hostApi.isTextDirectionRTL,
                this._hostApi.isWVVersionBlocked,
                this._hostApi.isAppVersionBlocked,
                this._hostApi.isOSVersionBlocked,
                this._hostApi.geoLocationCode
            ]);
            this.isInCTARegion = isInCTARegion;
            this.isPrivacyConsentAccepted = isPrivacyConsentAccepted;
            this.isInternetConnectionAvailable = isInternetConnectionAvailable;
            this.networkCostConstraint = networkCostConstraint;
            this.sessionId = sessionId;
            this.hostAppVersion = hostAppVersion;
            this.userGeoName = userGeoName;
            this.userDisplayLocale = userDisplayLocale;
            this.isTextDirectionRTL = isTextDirectionRTL;
            this.isWVVersionBlocked = isWVVersionBlocked;
            this.isAppVersionBlocked = isAppVersionBlocked;
            this.isOSVersionBlocked = isOSVersionBlocked;
            this.geoLocationCode = geoLocationCode;
            if (await isFeatureEnabledInternal("Feature_PreDownloadInitializer")) {
                this.tryStartWidgetInitializer();
            }
            this._hostApi.addEventListener("networkstatuschanged", async () => {
                this.isInternetConnectionAvailable = this._hostApi ? await this._hostApi.isInternetConnectionAvailable : false;
                this.networkCostConstraint = this._hostApi ? await this._hostApi.networkCostConstraint : "optIn";
                this.fireEvent("networkstatuschanged");
            });
            const windowApi = await tryGetHostApi("Window", 1, 1);
            if (windowApi) {
                // On every shown event, refresh and report a sessionId change
                await windowApi.addEventListener("shown", async () => {
                    this.sessionId = this._hostApi ? await this._hostApi.sessionId : "";
                    this.fireEvent("sessionidchanged");
                });
            }
        }
    }
    async canDownloadWidgetInitializerInAdvance() {
        if (!(await isFeatureEnabledInternal("Feature_PreDownloadInitializer"))) {
            return false;
        }
        const { isInCTARegion, isPrivacyConsentAccepted, isInternetConnectionAvailable, networkCostConstraint } = this;
        let canDownloadIntializer = true;
        const isOnlineAccount = await getIsAccountAvailable().catch(() => { new Error("No Online Account available details found in prefetch widget initializer stage"); });
        if (isInCTARegion === null ||
            isPrivacyConsentAccepted === null ||
            isOnlineAccount === null ||
            isInternetConnectionAvailable === null ||
            networkCostConstraint === null) {
            canDownloadIntializer = false;
        }
        if (!isPrivacyConsentAccepted && isInCTARegion) {
            canDownloadIntializer = false;
        }
        const persistedIsMeteredConnectionUsageAllowed = window.localStorage.getItem("isMeteredConnectionUsageAllowed");
        const isMeteredConnectionUsageAllowed = persistedIsMeteredConnectionUsageAllowed === "true";
        (async () => {
            await setIsMeteredConnectionUsageAllowedForMigration(isMeteredConnectionUsageAllowed);
        })();
        // when we try to start to download the initiliazer, the current page should be empty-page
        if (!isInternetConnectionAvailable || (isInCTARegion && !isMeteredConnectionUsageAllowed && (networkCostConstraint === "conservative" || networkCostConstraint === "optIn"))) {
            canDownloadIntializer = false;
        }
        return canDownloadIntializer;
    }
    async tryStartWidgetInitializer() {
        const canDownloadIntializer = await this.canDownloadWidgetInitializerInAdvance();
        if (canDownloadIntializer) {
            fetchWidgetIntializer(); // start to download the widget initializer
        }
    }
    fireEvent(eventName) {
        const listenerList = this._eventListeners.get(eventName);
        for (let index = 0; index < listenerList.length; index++) {
            listenerList[index]();
        }
    }
}
let g_DashboardHostApi;
export async function initDashboardHostApiInternal() {
    if (!g_DashboardHostApi) {
        g_DashboardHostApi = new DashboardHostApi();
        await g_DashboardHostApi.initAsync();
    }
    return g_DashboardHostApi;
}
export function getDashboardHostApiInternal() {
    return g_DashboardHostApi;
}
